import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/Chips";
import { FreshnessIndicator, StaticUSMap } from "@/components/MotiveShared";
import { CoachMap } from "@/components/CoachMap";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { assetDisplay } from "@/lib/assetDisplay";

const SEV_COLOR = {
  normal: "var(--brand-lime)",
  watch: "var(--status-hold)",
  attention: "var(--status-maintenance)",
};

const Tile = ({ label, value, unit }) => (
  <div className="p-4 rounded-md border" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}>
    <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>{label}</div>
    <div className="mt-1 tabular font-display text-2xl" style={{ color: "var(--fg-primary)" }}>
      {value ?? "—"}{unit && <span className="text-[13px] ml-1" style={{ color: "var(--fg-muted)" }}>{unit}</span>}
    </div>
  </div>
);

export default function VehicleOperationsTab({ assetId }) {
  const [vehicle, setVehicle] = useState(null);
  const [meta, setMeta] = useState(null);
  const [watch, setWatch] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/motive/vehicles/${assetId}`),
      api.get(`/motive/ops-watch/vehicle/${assetId}`),
    ]).then(([v, w]) => {
      if (cancelled) return;
      setVehicle(v.data.data);
      setMeta(v.data.meta);
      setWatch(w.data.data || []);
    }).catch((e) => {
      if (!cancelled) setErr(e?.response?.data?.detail || "Motive not available for this asset");
    });
    return () => { cancelled = true; };
  }, [assetId]);

  if (err) {
    return <div className="p-8 text-center" style={{ color: "var(--fg-secondary)" }} data-testid="vehicle-ops-empty">
      {err}
    </div>;
  }
  if (!vehicle) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-3 rounded-md border p-4" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}>
          <CoachMap lat={null} lng={null} className="h-[240px] md:h-[320px]" />
        </div>
        <div className="lg:col-span-3 p-4 text-center text-[13px]" style={{ color: "var(--fg-muted)" }}>
          Loading Motive telemetry…
        </div>
      </div>
    );
  }

  const t = vehicle.telemetry || {};
  const cd = vehicle.current_driver;
  const mismatch = vehicle.assignment_mismatch;
  const allAssn = vehicle.all_access_assigned_driver;
  const pinColor = t.ignition_on ? "var(--brand-lime)" : "var(--status-hold)";

  return (
    <div className="space-y-4" data-testid="vehicle-operations-tab">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
          Motive · Vehicle Operations · Read-only (Demo)
        </div>
        <FreshnessIndicator lastUpdatedAt={meta?.last_updated_at} ageSeconds={meta?.age_seconds} freshness={meta?.freshness} />
      </div>

      {/* Row 1: map + telemetry */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 rounded-md border p-4" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}>
          <CoachMap
            lat={t.lat}
            lng={t.lng}
            height={undefined}
            className="h-[240px] md:h-[320px]"
            popupContent={
              <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {assetDisplay(vehicle?.asset || vehicle) || "Coach"}
                </div>
                <div style={{ fontSize: 12, color: "#4A5157" }}>
                  {t.location_label || "—"}
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Speed: {t.speed_mph ?? "—"} mph
                </div>
                {meta?.last_updated_at && (
                  <div style={{ fontSize: 11, color: "#6B747C", marginTop: 4 }}>
                    Updated {meta.freshness || "recently"}
                  </div>
                )}
              </div>
            }
          />
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-3">
          <Tile label="Speed" value={t.speed_mph} unit="mph" />
          <Tile label="Odometer" value={t.odometer_mi?.toLocaleString?.()} unit="mi" />
          <Tile label="Fuel" value={t.fuel_pct} unit="%" />
          <Tile label="Range" value={t.range_mi} unit="mi" />
        </div>
      </div>

      {/* Row 2: current driver */}
      {cd !== undefined && (
        <div className="rounded-md border p-4 flex flex-col md:flex-row md:items-center gap-3" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }} data-testid="vehicle-current-driver">
          {cd ? (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Current Driver</div>
                <div className="text-[16px] font-semibold mt-0.5">
                  <Link to={`/drivers/${allAssn?.driver_id || ''}?tab=operations`} className="hover:underline" style={{ color: "var(--fg-primary)" }} data-testid="current-driver-link">
                    {cd.display_name}
                  </Link>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <StatusPill status={cd.current_duty_status} />
                  <span className="text-[12px]" style={{ color: "var(--fg-secondary)" }}>on {assetDisplay(vehicle)}</span>
                </div>
                <div className="mt-2 text-[12.5px]" style={{ color: "var(--fg-secondary)" }}>
                  {mismatch ? (
                    <span className="inline-flex items-center gap-1.5" style={{ color: "var(--status-hold)" }} data-testid="assignment-mismatch">
                      <AlertTriangle size={12} /> Expected: <strong>{mismatch.expected}</strong> · Motive Reports: <strong>{mismatch.motive_reports}</strong>
                    </span>
                  ) : allAssn ? (
                    <span className="inline-flex items-center gap-1.5" style={{ color: "var(--brand-lime)" }} data-testid="assignment-match">
                      <CheckCircle2 size={12} /> Assigned Driver Matches Motive
                    </span>
                  ) : null}
                </div>
              </div>
              <div>
                <Link to={`/drivers/${allAssn?.driver_id || ''}?tab=operations`}
                  className="inline-flex items-center rounded-md px-3 py-2 text-[13px]"
                  style={{ borderColor: "var(--brand-blue)", background: "var(--brand-blue)", color: "white" }}
                  data-testid="view-driver-ops">
                  View Driver Operations →
                </Link>
              </div>
            </>
          ) : (
            <div className="p-2 flex items-center gap-2" style={{ color: "var(--status-hold)" }} data-testid="no-current-driver">
              <AlertTriangle size={14} /> NO CURRENT DRIVER REPORTED BY MOTIVE
            </div>
          )}
        </div>
      )}

      {/* Row 3: Ops Watch */}
      <div className="rounded-md border" style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }} data-testid="ops-watch-panel">
        <div className="px-4 py-2 text-[11px] uppercase tracking-widest" style={{ color: "var(--fg-muted)", borderBottom: "1px solid var(--border-subtle)" }}>Operations Watch</div>
        <ul>
          {watch.length === 0 || (watch.length === 1 && watch[0].severity === "normal") ? (
            <li className="px-4 py-3 text-[13px] inline-flex items-center gap-2" style={{ color: "var(--brand-lime)" }}>
              <CheckCircle2 size={14} /> All systems normal
            </li>
          ) : watch.map((w, i) => (
            <li key={i} className="px-4 py-3 flex items-center gap-3 text-[13px]" style={{ borderTop: i > 0 ? "1px solid var(--border-subtle)" : "none" }}
              data-testid={`ops-watch-${w.severity}`}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: SEV_COLOR[w.severity] || "var(--fg-muted)" }} />
              <span className="uppercase tracking-widest text-[10.5px] font-semibold" style={{ color: SEV_COLOR[w.severity] }}>{w.severity}</span>
              <strong style={{ color: "var(--fg-primary)" }}>{w.label}</strong>
              <span style={{ color: "var(--fg-secondary)" }}>· {w.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="text-[11.5px]" style={{ color: "var(--fg-muted)" }}>
        Data displayed is read-only. Update in Motive.
      </div>
    </div>
  );
}
